// Updated BillHistory.jsx
import React, { useEffect, useMemo, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { format } from "date-fns";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
  LabelList,
} from "recharts";
import {
  Card,
  Table,
  Tabs,
  DatePicker,
  Select,
  Input,
  InputNumber,
  Button,
  Space,
  Tag,
  Row,
  Col,
  Typography,
  Popconfirm,
} from "antd";
import {
  HomeOutlined,
  ArrowLeftOutlined,
  PrinterOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  StopOutlined,
} from "@ant-design/icons";

import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import fetchData from "../../functions/fetchData";
import logo from "../../assets/logo.png";
import BillItemModal from "../../components/Modals/BillItemModal";
import { getUserType } from "../../utility/auth";
import cancelBill from "../../functions/cancelBill";
import { buildInvoiceBody, buildInvoiceDocument } from "./billInvoiceTemplate";
import { exportSalesSummaryPdf, exportInvoicesPdf } from "./billReportPdf";

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

const PAYMENT_TAG_COLOR = {
  Cash: "green",
  Credit: "orange",
  "Bank Transfer": "blue",
  Entertainment: "magenta",
  UPI: "purple",
  "QR Code": "cyan",
};

export default function BillHistory() {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [orderNoSearch, setOrderNoSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [activeTab, setActiveTab] = useState("active");
  const [tableFilter, setTableFilter] = useState("");
  const [amountFilter, setAmountFilter] = useState({ min: "", max: "" });
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [customers, setCustomers] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pdfOrder, setPdfOrder] = useState("asc"); // order of bills inside the summary PDF
  const [bulkBusy, setBulkBusy] = useState(""); // "", "print" or "pdf"

  const navigate = useNavigate();
  const userType = getUserType();
  const isCashier = userType?.toLowerCase() === 'cashier';

  const customerNameById = useMemo(() => {
    const map = {};
    customers.forEach((c) => { map[c.id] = c.name; });
    return map;
  }, [customers]);

  useEffect(() => {
    fetchData("customers", setCustomers, "id", {}).catch((error) => {
      console.error("Error fetching customers:", error);
    });
  }, []);

  const fetchAllData = async () => {
    try {
      await fetchData("final_bill", setData, "id", {});
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const handleCancelBill = async (row) => {
    try {
      await cancelBill("final_bill", "id", row.id);
      toast.success(`Bill #${row.id} cancelled successfully`);
      await fetchAllData();
    } catch (error) {
      console.error("Error cancelling bill:", error);
      toast.error("Failed to cancel bill");
    }
  };

  // Initial data load effect
  useEffect(() => {
    if (data.length > 0) {
      applyFilters();
    }
  }, [data]);

  // Filter change effect
  useEffect(() => {
    if (data.length > 0) {
      applyFilters();
    }
  }, [startDate, endDate, paymentMode, activeTab, orderNoSearch, tableFilter, amountFilter, sortBy, sortOrder]);

  const applyFilters = () => {
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      toast.error("Invalid date range: Start Date is after End Date");
      return;
    }

    let filtered = [...data];

    // Filter by status (active/cancelled)
    if (activeTab === "cancelled") {
      filtered = filtered.filter(item => item.status === 2 || item.status === "2");
    } else {
      filtered = filtered.filter(item => item.status !== 2 && item.status !== "2");
    }

    // Filter by date range (fall back to inv_date when setup_date wasn't recorded)
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999); // Include the entire end date

      filtered = filtered.filter(item => {
        const rawDate = item.setup_date || item.inv_date;
        if (!rawDate) return false;
        const itemDate = new Date(rawDate);
        return itemDate >= start && itemDate <= end;
      });
    } else if (startDate) {
      const start = new Date(startDate);
      filtered = filtered.filter(item => {
        const rawDate = item.setup_date || item.inv_date;
        if (!rawDate) return false;
        const itemDate = new Date(rawDate);
        return itemDate >= start;
      });
    } else if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter(item => {
        const rawDate = item.setup_date || item.inv_date;
        if (!rawDate) return false;
        const itemDate = new Date(rawDate);
        return itemDate <= end;
      });
    }

    // Filter by payment mode
    if (paymentMode) {
      filtered = filtered.filter(item => item.payment_mode === paymentMode);
    }

    // Filter by invoice number
    if (orderNoSearch) {
      filtered = filtered.filter(item =>
        item.id.toString().includes(orderNoSearch.toLowerCase())
      );
    }

    // Filter by table
    if (tableFilter) {
      filtered = filtered.filter(item =>
        item.table_number && item.table_number.toLowerCase().includes(tableFilter.toLowerCase())
      );
    }

    // Filter by amount range
    if (amountFilter.min || amountFilter.max) {
      filtered = filtered.filter(item => {
        const amount = parseFloat(item.grand_total || 0);
        const minAmount = parseFloat(amountFilter.min || 0);
        const maxAmount = parseFloat(amountFilter.max || Infinity);
        return amount >= minAmount && amount <= maxAmount;
      });
    }

    // Sort data
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case "date":
          aValue = new Date((a.setup_date || a.inv_date) + " " + (a.inv_time || "00:00:00"));
          bValue = new Date((b.setup_date || b.inv_date) + " " + (b.inv_time || "00:00:00"));
          break;
        case "amount":
          aValue = parseFloat(a.grand_total || 0);
          bValue = parseFloat(b.grand_total || 0);
          break;
        case "table":
          aValue = a.table_number || "";
          bValue = b.table_number || "";
          break;
        case "invoice":
          aValue = parseInt(a.id || 0);
          bValue = parseInt(b.id || 0);
          break;
        default:
          aValue = a.id;
          bValue = b.id;
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredData(filtered);
  };

  const clearFilters = () => {
    setStartDate("");
    setEndDate("");
    setPaymentMode("");
    setOrderNoSearch("");
    setTableFilter("");
    setAmountFilter({ min: "", max: "" });
    setSortBy("date");
    setSortOrder("desc");
    localStorage.removeItem("startDate");
    localStorage.removeItem("endDate");
    localStorage.removeItem("paymentMode");
  };

  const exportCSV = () => {
    const rows = filteredData.length > 0 ? filteredData : data;
    if (rows.length === 0) {
      toast.error("No data to export");
      return;
    }
    const headers = Object.keys(rows[0]);
    const csvLines = [
      headers.join(","),
      ...rows.map(row =>
        headers.map(h => `"${String(row[h] ?? "").replace(/"/g, '""')}"`).join(",")
      ),
    ];
    const blob = new Blob([csvLines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "salereport.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportPDF = async () => {
    if (filteredData.length === 0) {
      toast.error("No data to export");
      return;
    }
    const { companyProfile } = await loadInvoiceContext();
    const dates = filteredData
      .map((b) => new Date(b.setup_date || b.inv_date))
      .filter((d) => !isNaN(d))
      .sort((a, b) => a - b);
    const from = startDate || (dates.length ? format(dates[0], "yyyy-MM-dd") : "");
    const to = endDate || (dates.length ? format(dates[dates.length - 1], "yyyy-MM-dd") : "");
    const billTime = (b) =>
      new Date(`${b.setup_date || b.inv_date} ${b.inv_time || "00:00:00"}`).getTime() || 0;
    const dir = pdfOrder === "desc" ? -1 : 1;
    const orderedBills = [...filteredData].sort(
      (a, b) => dir * (billTime(a) - billTime(b) || (parseInt(a.id) || 0) - (parseInt(b.id) || 0))
    );
    exportSalesSummaryPdf({
      bills: orderedBills,
      logo,
      companyName: companyProfile?.name || "StoreHub",
      periodLabel: from || to ? `${from || "..."}  to  ${to || "..."}` : "All dates",
      statusLabel: activeTab === "cancelled" ? "Cancelled Bills" : "Active Bills",
    });
  };

  const handleViewItems = (row) => {
    setSelectedBill(row);
    setShowModal(true);
  };

  // ---- invoice printing / bulk download (shared A4 template) ----
  const loadInvoiceContext = async () => {
    let companyProfile = {};
    try {
      const companyResponse = await fetchData("company_profile", () => {}, "id", {});
      if (companyResponse && companyResponse.length > 0) companyProfile = companyResponse[0];
    } catch (error) {
      console.log("Using default company info", error);
    }
    let showVatDetails = false;
    try {
      const coreSettingResponse = await fetchData("coresetting", () => {}, "id", {});
      if (coreSettingResponse && coreSettingResponse.length > 0) {
        showVatDetails = coreSettingResponse[0].showVat?.toLowerCase() === "yes";
      }
    } catch (error) {
      console.log("Could not fetch core settings, defaulting showVat to false", error);
    }
    return { companyProfile, showVatDetails };
  };

  const buildInvoiceMarkup = async (billData, ctx) => {
    const itemsResponse =
      (await fetchData("order_items", () => {}, "order_id", { order_id: billData.id })) || [];

    // final_bill only stores customer_id; use the already loaded customers when possible
    let customerDetails = {};
    if (billData.customer_id) {
      customerDetails = customers.find((c) => c.id === billData.customer_id);
      if (!customerDetails) {
        try {
          const res = await fetchData("customers", () => {}, "id", { id: billData.customer_id });
          customerDetails = res && res.length > 0 ? res[0] : {};
        } catch (error) {
          console.log("Could not fetch customer details", error);
          customerDetails = {};
        }
      }
    }

    const invoiceData = {
      invoice_number: billData.id,
      setup_date: billData.setup_date || billData.inv_date,
      inv_time: billData.inv_time,
      payment_method: billData.payment_mode,
      customer_name: customerDetails.name || "Walk-in Customer",
      customer_phone: customerDetails.contact || "N/A",
      customer_email: customerDetails.email || "",
      customer_address: customerDetails.address || "",
      customer_gst: customerDetails.taxid || "",
      items: itemsResponse.map((item) => ({
        item_name: item.item_name,
        description: item.description || "",
        rate: item.rate || 0,
        quantity: item.quantity,
        discount_percent: item.discount_percent || 0,
        vat: item.vat || 7,
        total_amount: item.total_price,
        offerprice: item.rate || 0,
        iname: item.item_name,
      })),
      subtotal: billData.subtotal,
      discount_value: billData.discount_value || 0,
      tax: billData.tax,
      round_off: billData.round_off || 0,
      grand_total: billData.grand_total,
      total: billData.subtotal,
    };
    return buildInvoiceBody(invoiceData, ctx);
  };

  // Builds the HTML of many bills (fetching 5 at a time) into one document.
  const buildBillsDocument = async (bills, title) => {
    const ctx = await loadInvoiceContext();
    const bodies = [];
    for (let i = 0; i < bills.length; i += 5) {
      const chunk = await Promise.all(bills.slice(i, i + 5).map((b) => buildInvoiceMarkup(b, ctx)));
      bodies.push(...chunk);
    }
    return buildInvoiceDocument(bodies, title);
  };

  // Single or bulk print: one window, one invoice per page.
  const printBills = async (bills) => {
    if (!bills.length) return;
    // open synchronously so the browser does not block the popup
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error("Popup blocked. Please allow popups to print.");
      return;
    }
    printWindow.document.write("<p style='font-family:sans-serif;padding:20px'>Preparing bills...</p>");
    setBulkBusy("print");
    try {
      const html = await buildBillsDocument(
        bills,
        bills.length === 1 ? `VAT Invoice - ${bills[0].id}` : `Bills (${bills.length})`
      );
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 800);
    } catch (error) {
      printWindow.close();
      toast.error("Error printing invoice");
      console.error("Print error:", error);
    } finally {
      setBulkBusy("");
    }
  };

  const printBillVATFormat = (billData) => printBills([billData]);
  const printVATInvoice = printBillVATFormat; // Legacy function name for compatibility

  const selectedBills = filteredData.filter((b) => selectedRowKeys.includes(b.id));

  const handleBulkPrint = () => {
    if (!selectedBills.length) return toast.info("Select at least one bill");
    printBills(selectedBills);
  };

  const handleBulkPdf = async () => {
    if (!selectedBills.length) return toast.info("Select at least one bill");
    setBulkBusy("pdf");
    const toastId = toast.loading(`Creating PDF (0/${selectedBills.length})...`);
    try {
      const html = await buildBillsDocument(selectedBills, "Bills");
      await exportInvoicesPdf(
        html,
        `bills_${selectedBills.length}_${dayjs().format("YYYYMMDD_HHmm")}.pdf`,
        (done, total) => toast.update(toastId, { render: `Creating PDF (${done}/${total})...` })
      );
      toast.update(toastId, { render: "PDF downloaded", type: "success", isLoading: false, autoClose: 2500 });
    } catch (error) {
      console.error("Bulk PDF error:", error);
      toast.update(toastId, { render: "Failed to create PDF", type: "error", isLoading: false, autoClose: 3000 });
    } finally {
      setBulkBusy("");
    }
  };

  const monthlyData = (() => {
    const summary = {};
    filteredData.forEach((item) => {
      const d = new Date(item.setup_date || item.inv_date);
      if (isNaN(d)) return;
      const key = format(d, "yyyy-MM");
      summary[key] = summary[key] || { month: format(d, "MMM yyyy"), total: 0, bills: 0 };
      summary[key].total += parseFloat(item.grand_total || 0);
      summary[key].bills += 1;
    });
    return Object.keys(summary)
      .sort()
      .map((k) => ({ ...summary[k], total: Math.round(summary[k].total * 100) / 100 }));
  })();
  const bestMonth = monthlyData.reduce((m, d) => (!m || d.total > m.total ? d : m), null);


  const totalSubtotal = filteredData.reduce((acc, item) => acc + parseFloat(item.subtotal_afterdiscount || 0), 0);
  const totalTax = filteredData.reduce((acc, item) => acc + parseFloat(item.tax || 0), 0);
  const totalGrand = filteredData.reduce((acc, item) => acc + parseFloat(item.grand_total || 0), 0);

  const tableColumns = [
    {
      title: "Inv. No.",
      dataIndex: "id",
      key: "id",
      align: "center",
      sorter: (a, b) => (parseInt(a.id) || 0) - (parseInt(b.id) || 0),
      render: (id, row) => (
        <Button
          type="link"
          style={{ padding: 0, fontWeight: 700, color: "#1677ff" }}
          onClick={() => handleViewItems(row)}
          title="View bill details"
        >
          #{id}
        </Button>
      ),
    },
    {
      title: "Customer",
      dataIndex: "customer_id",
      key: "customer_id",
      align: "center",
      render: (customerId) => (
        <Tag color="geekblue" style={{ borderRadius: 12 }}>
          {customerNameById[customerId] || "Walk-in Customer"}
        </Tag>
      ),
    },
    {
      title: "Inv Date",
      dataIndex: "inv_date",
      key: "inv_date",
      align: "center",
      sorter: (a, b) => String(a.inv_date || "").localeCompare(String(b.inv_date || "")),
      render: (v) => <Text type="secondary">{v || "-"}</Text>,
    },
    {
      title: "Setup Date",
      dataIndex: "setup_date",
      key: "setup_date",
      align: "center",
      sorter: (a, b) => String(a.setup_date || "").localeCompare(String(b.setup_date || "")),
      render: (v) => <Text type="secondary">{v || "-"}</Text>,
    },
    {
      title: "Time",
      dataIndex: "inv_time",
      key: "inv_time",
      align: "center",
      render: (v) => <Text type="secondary">{v || "-"}</Text>,
    },
    {
      title: "Subtotal",
      dataIndex: "subtotal_afterdiscount",
      key: "subtotal_afterdiscount",
      align: "right",
      sorter: (a, b) => parseFloat(a.subtotal_afterdiscount || 0) - parseFloat(b.subtotal_afterdiscount || 0),
      render: (v) => <Text style={{ color: "#1677ff" }}>{`฿ ${parseFloat(v || 0).toFixed(2)}`}</Text>,
    },
    {
      title: "Tax",
      dataIndex: "tax",
      key: "tax",
      align: "right",
      sorter: (a, b) => parseFloat(a.tax || 0) - parseFloat(b.tax || 0),
      render: (v) => <Text style={{ color: "#fa8c16" }}>{`฿ ${parseFloat(v || 0).toFixed(2)}`}</Text>,
    },
    {
      title: "Grand Total",
      dataIndex: "grand_total",
      key: "grand_total",
      align: "right",
      sorter: (a, b) => parseFloat(a.grand_total || 0) - parseFloat(b.grand_total || 0),
      render: (v) => <Text strong style={{ color: "#2e7d32" }}>{`฿ ${parseFloat(v || 0).toFixed(2)}`}</Text>,
    },
    {
      title: "Payment",
      dataIndex: "payment_mode",
      key: "payment_mode",
      align: "center",
      render: (mode) => <Tag color={PAYMENT_TAG_COLOR[mode] || "default"}>{mode || "N/A"}</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, row) => (
        <Space>
          <Button size="small" type="primary" icon={<PrinterOutlined />} onClick={() => printVATInvoice(row)} title="Print A4 VAT Format">
            Print
          </Button>
          {row.status !== 2 && row.status !== "2" && (
            <Popconfirm
              title="Cancel this bill?"
              description="This will mark the bill as cancelled and remove its ledger entries."
              okText="Yes, Cancel"
              okType="danger"
              cancelText="No"
              onConfirm={() => handleCancelBill(row)}
            >
              <Button size="small" danger icon={<StopOutlined />} title="Cancel Bill">
                Cancel
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // Navigation functions for cashiers
  const navigateToDashboard = () => {
    navigate('/dashboard');
  };

  const navigateBack = () => {
    navigate(-1);
  };

  // Check if mobile device
  const isMobile = window.innerWidth <= 768;

  // Render content without Layout/Header for cashiers
  const renderContent = () => (
    <>
      <ToastContainer />
      <style>{`
        .bh-row-even td { background-color: #fafcff; }
        .bh-row-odd td { background-color: #ffffff; }
      `}</style>

      {/* Cashier Navigation Header */}
      {isCashier && (
        <div style={{
          backgroundColor: '#1f1f1f',
          color: 'white',
          padding: isMobile ? '10px 15px' : '14px 20px',
          marginBottom: '20px'
        }}>
          <Row justify="space-between" align="middle" gutter={[8, 8]}>
            <Col>
              <Space size={12}>
                <Button ghost icon={<ArrowLeftOutlined />} onClick={navigateBack} size={isMobile ? "small" : "middle"}>
                  Back
                </Button>
                <Title level={isMobile ? 5 : 4} style={{ color: 'white', margin: 0 }}>
                  Sales Reports
                </Title>
              </Space>
            </Col>
            <Col>
              <Button ghost icon={<HomeOutlined />} onClick={navigateToDashboard} size={isMobile ? "small" : "middle"}>
                Dashboard
              </Button>
            </Col>
          </Row>
        </div>
      )}

      <div style={{ padding: isCashier ? '0 20px 20px' : 0 }}>
        <Card bordered={false} style={{ borderRadius: 12, marginBottom: 16 }}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: "active",
                label: (
                  <span><CheckCircleOutlined /> Active Bills</span>
                ),
              },
              {
                key: "cancelled",
                label: (
                  <span><CloseCircleOutlined /> Cancelled Bills</span>
                ),
              },
            ]}
          />

          <Row gutter={[12, 12]}>
            <Col xs={24} sm={12} md={8}>
              <Text type="secondary" style={{ fontSize: 12 }}>Date Range</Text>
              <RangePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                allowEmpty={[true, true]}
                value={[startDate ? dayjs(startDate) : null, endDate ? dayjs(endDate) : null]}
                onChange={(_, dateStrings) => {
                  setStartDate(dateStrings?.[0] || "");
                  setEndDate(dateStrings?.[1] || "");
                }}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Text type="secondary" style={{ fontSize: 12 }}>Payment Mode</Text>
              <Select
                style={{ width: "100%" }}
                value={paymentMode || undefined}
                placeholder="All"
                allowClear
                onChange={(v) => setPaymentMode(v || "")}
                options={[
                  { value: "Cash", label: "Cash" },
                  { value: "Credit", label: "Credit" },
                  { value: "Bank Transfer", label: "Bank" },
                  { value: "Entertainment", label: "Entertainment" },
                  { value: "UPI", label: "UPI" },
                  { value: "QR Code", label: "QR Code" },
                ]}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Text type="secondary" style={{ fontSize: 12 }}>Invoice No.</Text>
              <Input
                value={orderNoSearch}
                onChange={(e) => setOrderNoSearch(e.target.value)}
                placeholder="Search..."
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Text type="secondary" style={{ fontSize: 12 }}>Table</Text>
              <Input
                value={tableFilter}
                onChange={(e) => setTableFilter(e.target.value)}
                placeholder="Table no."
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Text type="secondary" style={{ fontSize: 12 }}>Sort By</Text>
              <Select
                style={{ width: "100%" }}
                value={sortBy}
                onChange={setSortBy}
                options={[
                  { value: "date", label: "Date & Time" },
                  { value: "amount", label: "Amount" },
                  { value: "table", label: "Table" },
                  { value: "invoice", label: "Invoice" },
                ]}
              />
            </Col>
          </Row>

          <Row gutter={[12, 12]} style={{ marginTop: 12 }} align="bottom">
            <Col xs={24} sm={12} md={4}>
              <Text type="secondary" style={{ fontSize: 12 }}>Min Amount</Text>
              <InputNumber
                style={{ width: "100%" }}
                value={amountFilter.min}
                onChange={(v) => setAmountFilter(prev => ({ ...prev, min: v ?? "" }))}
                placeholder="Min ฿"
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Text type="secondary" style={{ fontSize: 12 }}>Max Amount</Text>
              <InputNumber
                style={{ width: "100%" }}
                value={amountFilter.max}
                onChange={(v) => setAmountFilter(prev => ({ ...prev, max: v ?? "" }))}
                placeholder="Max ฿"
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Text type="secondary" style={{ fontSize: 12 }}>Sort Order</Text>
              <Select
                style={{ width: "100%" }}
                value={sortOrder}
                onChange={setSortOrder}
                options={[
                  { value: "desc", label: "Newest First" },
                  { value: "asc", label: "Oldest First" },
                ]}
              />
            </Col>
            <Col xs={24} md={12}>
              <Space wrap>
                <Button icon={<ReloadOutlined />} onClick={clearFilters}>Clear</Button>
                <Button icon={<FileExcelOutlined />} onClick={exportCSV}>CSV</Button>
                <Select
                  style={{ width: 170 }}
                  value={pdfOrder}
                  onChange={setPdfOrder}
                  options={[
                    { value: "asc", label: "PDF: Oldest first (ASC)" },
                    { value: "desc", label: "PDF: Newest first (DESC)" },
                  ]}
                />
                <Button danger icon={<FilePdfOutlined />} onClick={exportPDF}>Summary PDF</Button>
                <Tag color="blue" style={{ padding: "4px 10px", fontSize: 13 }}>
                  {filteredData.length} / {data.length} Bills
                </Tag>
              </Space>
            </Col>
          </Row>
        </Card>

        <Card bordered={false} style={{ borderRadius: 12 }}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 8,
              marginBottom: 12,
              padding: "8px 12px",
              borderRadius: 8,
              background: selectedRowKeys.length ? "#e6f4ff" : "#fafafa",
              border: "1px solid " + (selectedRowKeys.length ? "#91caff" : "#f0f0f0"),
            }}
          >
            <Text strong>
              {selectedRowKeys.length
                ? `${selectedRowKeys.length} bill${selectedRowKeys.length > 1 ? "s" : ""} selected`
                : "Select bills to print or download together"}
            </Text>
            <Button
              size="small"
              onClick={() => setSelectedRowKeys(filteredData.map((b) => b.id))}
              disabled={!filteredData.length}
            >
              Select all {filteredData.length}
            </Button>
            <Button size="small" onClick={() => setSelectedRowKeys([])} disabled={!selectedRowKeys.length}>
              Clear selection
            </Button>
            <div style={{ flex: 1 }} />
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={handleBulkPrint}
              loading={bulkBusy === "print"}
              disabled={!selectedRowKeys.length || !!bulkBusy}
            >
              Print Selected
            </Button>
            <Button
              danger
              type="primary"
              icon={<FilePdfOutlined />}
              onClick={handleBulkPdf}
              loading={bulkBusy === "pdf"}
              disabled={!selectedRowKeys.length || !!bulkBusy}
            >
              Download Selected PDF
            </Button>
          </div>
          <Table
            rowKey="id"
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys,
              preserveSelectedRowKeys: true,
              selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT, Table.SELECTION_NONE],
            }}
            columns={tableColumns}
            dataSource={filteredData}
            pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `${t} bills` }}
            scroll={{ x: true }}
            rowClassName={(_, index) => (index % 2 === 0 ? "bh-row-even" : "bh-row-odd")}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={6} align="right">
                    <Text strong>{activeTab === "cancelled" ? "Cancelled Total" : "Total"}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right">
                    <Text strong style={{ color: "#1677ff" }}>฿ {totalSubtotal.toFixed(2)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right">
                    <Text strong style={{ color: "#fa8c16" }}>฿ {totalTax.toFixed(2)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={7} align="right">
                    <Text strong style={{ color: "#2e7d32" }}>฿ {totalGrand.toFixed(2)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={8} colSpan={2} />
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </Card>

        <Card bordered={false} style={{ borderRadius: 12, marginTop: 16 }}>
          <Row justify="space-between" align="middle" style={{ marginBottom: 12 }} gutter={[8, 8]}>
            <Col>
              <Title level={5} style={{ margin: 0 }}>Monthly Sales Summary</Title>
            </Col>
            <Col>
              <Space wrap>
                <Tag color="blue">{monthlyData.length} month{monthlyData.length === 1 ? "" : "s"}</Tag>
                {bestMonth && (
                  <Tag color="green">
                    Best: {bestMonth.month} - ฿ {bestMonth.total.toLocaleString("en-US", { maximumFractionDigits: 2 })}
                  </Tag>
                )}
              </Space>
            </Col>
          </Row>
          {monthlyData.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: "#8c8c8c" }}>No data for selected filters</div>
          ) : (
            <ResponsiveContainer width="100%" height={340}>
              <ComposedChart data={monthlyData} margin={{ top: 24, right: 16, left: 8, bottom: 4 }}>
                <defs>
                  <linearGradient id="bhSalesGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4096ff" stopOpacity={1} />
                    <stop offset="100%" stopColor="#1677ff" stopOpacity={0.55} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="month" tickLine={false} axisLine={{ stroke: "#d9d9d9" }} />
                <YAxis
                  yAxisId="sales"
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(v % 1000 ? 1 : 0)}k` : v)}
                />
                <YAxis yAxisId="bills" orientation="right" tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                  cursor={{ fill: "rgba(22,119,255,0.06)" }}
                  formatter={(value, name) =>
                    name === "Sales"
                      ? [`฿ ${Number(value).toLocaleString("en-US", { minimumFractionDigits: 2 })}`, name]
                      : [value, name]
                  }
                />
                <Legend />
                <Bar yAxisId="sales" dataKey="total" name="Sales" fill="url(#bhSalesGradient)" radius={[6, 6, 0, 0]} maxBarSize={56}>
                  <LabelList
                    dataKey="total"
                    position="top"
                    style={{ fontSize: 11, fill: "#595959" }}
                    formatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v)}
                  />
                </Bar>
                <Line
                  yAxisId="bills"
                  type="monotone"
                  dataKey="bills"
                  name="Bills"
                  stroke="#fa8c16"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {showModal && (
        <BillItemModal
          isOpen={showModal}
          bill={selectedBill}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );

  // Main return statement with conditional rendering
  return isCashier ? (
    <div style={{
      width: '100vw',
      height: '100vh',
      backgroundColor: '#f5f5f5',
      overflow: 'auto'
    }}>
      {renderContent()}
    </div>
  ) : (
    <Layout>
      <Header title="Sale Report" />
      <div style={{ padding: 20 }}>
        {renderContent()}
      </div>
    </Layout>
  );
}
